/**
 * 影片觀看進度記憶管理器
 * 負責保存和恢復用戶的影片觀看進度
 */

import { getFirebaseServices } from '../firebase/config';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

// 獲取 Firestore 實例
let db: any = null;
getFirebaseServices().then(services => {
  db = services.db;
});

// 觀看進度數據結構
export interface WatchProgress {
  videoId: string;
  videoUrl: string;
  currentTime: number; // 當前播放時間（秒）
  duration: number; // 影片總長度（秒）
  progress: number; // 播放進度（0-1）
  lastWatched: string; // 最後觀看時間（ISO 字符串）
  watchCount: number; // 觀看次數
  isCompleted: boolean; // 是否已看完（進度 > 90%）
  userId: string; // 用戶 ID
}

// 本地存儲的觀看進度（用於離線使用）
export interface LocalWatchProgress {
  [videoId: string]: {
    currentTime: number;
    duration: number;
    progress: number;
    lastWatched: string;
    watchCount: number;
    isCompleted: boolean;
  };
}

class WatchProgressManager {
  private static readonly STORAGE_KEY = 'watchProgress';
  private static readonly SYNC_INTERVAL = 30000; // 30秒同步一次
  private static readonly MIN_PROGRESS_TO_SAVE = 5; // 至少觀看5秒才保存
  private static readonly COMPLETION_THRESHOLD = 0.9; // 90%視為看完

  /**
   * 保存觀看進度到本地存儲
   */
  static saveProgressLocally(videoId: string, progress: Omit<WatchProgress, 'videoId' | 'userId'>): void {
    try {
      const localProgress = this.getLocalProgress();
      
      localProgress[videoId] = {
        currentTime: progress.currentTime,
        duration: progress.duration,
        progress: progress.progress,
        lastWatched: progress.lastWatched,
        watchCount: progress.watchCount,
        isCompleted: progress.isCompleted
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(localProgress));
      // console.log(`💾 本地保存觀看進度: ${videoId} - ${Math.floor(progress.currentTime)}秒`);
    } catch (error) {
      console.warn('⚠️ 保存本地觀看進度失敗:', error);
    }
  }

  /**
   * 從本地存儲獲取觀看進度
   */
  static getLocalProgress(): LocalWatchProgress {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('⚠️ 讀取本地觀看進度失敗:', error);
      return {};
    }
  }

  /**
   * 獲取特定影片的本地觀看進度
   */
  static getVideoProgress(videoId: string): LocalWatchProgress[string] | null {
    const localProgress = this.getLocalProgress();
    return localProgress[videoId] || null;
  }

  /**
   * 保存觀看進度到 Firestore
   */
  static async saveProgressToFirestore(
    userId: string, 
    videoId: string, 
    progress: Omit<WatchProgress, 'videoId' | 'userId'>
  ): Promise<void> {
    try {
      if (!userId || !videoId) {
        console.warn('⚠️ 用戶ID或影片ID為空，無法保存觀看進度');
        return;
      }

      const { db } = await getFirebaseServices();
      const progressData: WatchProgress = {
        videoId,
        userId,
        ...progress,
        lastWatched: new Date().toISOString()
      };

      const progressRef = doc(db, 'watchProgress', `${userId}_${videoId}`);
      await setDoc(progressRef, {
        ...progressData,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // console.log(`☁️ Firestore 保存觀看進度: ${videoId} - ${Math.floor(progress.currentTime)}秒`);
    } catch (error) {
      console.error('❌ 保存觀看進度到 Firestore 失敗:', error);
    }
  }

  /**
   * 從 Firestore 獲取觀看進度
   */
  static async getProgressFromFirestore(userId: string, videoId: string): Promise<WatchProgress | null> {
    try {
      if (!userId || !videoId) {
        return null;
      }

      const { db } = await getFirebaseServices();
      const progressRef = doc(db, 'watchProgress', `${userId}_${videoId}`);
      const progressSnap = await getDoc(progressRef);
      
      if (progressSnap.exists()) {
        const data = progressSnap.data();
        return {
          videoId: data.videoId,
          userId: data.userId,
          currentTime: data.currentTime,
          duration: data.duration,
          progress: data.progress,
          lastWatched: data.lastWatched,
          watchCount: data.watchCount,
          isCompleted: data.isCompleted
        };
      }
      
      return null;
    } catch (error) {
      console.error('❌ 從 Firestore 獲取觀看進度失敗:', error);
      return null;
    }
  }

  /**
   * 更新觀看次數
   */
  static async updateWatchCount(userId: string, videoId: string): Promise<void> {
    try {
      // 確保 db 已初始化
      if (!db) {
        const services = await getFirebaseServices();
        db = services.db;
      }
      
      const progressRef = doc(db, 'watchProgress', `${userId}_${videoId}`);
      
      // 🔧 修復：先獲取現有的觀看次數，然後正確遞增
      const currentDoc = await getDoc(progressRef);
      const currentWatchCount = currentDoc.exists() ? (currentDoc.data()?.watchCount || 0) : 0;
      const newWatchCount = currentWatchCount + 1;
      
      await updateDoc(progressRef, {
        watchCount: newWatchCount,  // 🔧 修復：使用正確的數字而不是 serverTimestamp()
        lastWatched: new Date().toISOString()
      });
      
      // 同時更新本地存儲
      const localProgress = this.getLocalProgress();
      if (localProgress[videoId]) {
        localProgress[videoId].watchCount = newWatchCount;
        localProgress[videoId].lastWatched = new Date().toISOString();
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(localProgress));
      }
      
      console.log(`📊 觀看次數已更新: ${videoId} -> ${newWatchCount} 次`);
    } catch (error) {
      console.error('❌ 更新觀看次數失敗:', error);
    }
  }

  /**
   * 計算觀看進度
   */
  static calculateProgress(currentTime: number, duration: number): number {
    if (duration <= 0) return 0;
    return Math.min(currentTime / duration, 1);
  }

  /**
   * 判斷是否應該保存進度
   */
  static shouldSaveProgress(currentTime: number, duration: number): boolean {
    // 🔧 修復：放寬保存條件，提高進度保存的穩定性
    // 至少觀看3秒，且不是剛開始（避免保存0秒），且不超過影片長度
    return currentTime >= 3 && currentTime > 0 && currentTime < duration && duration > 0;
  }

  /**
   * 判斷是否已看完影片
   */
  static isCompleted(currentTime: number, duration: number): boolean {
    const progress = this.calculateProgress(currentTime, duration);
    return progress >= this.COMPLETION_THRESHOLD;
  }

  /**
   * 🔧 新增：驗證觀看進度數據的一致性
   */
  static async validateProgressConsistency(userId: string, videoId: string): Promise<{
    localProgress: LocalWatchProgress[string] | null;
    cloudProgress: WatchProgress | null;
    isConsistent: boolean;
    recommendedAction: 'use_local' | 'use_cloud' | 'merge' | 'none';
  }> {
    try {
      const localProgress = this.getVideoProgress(videoId);
      const cloudProgress = userId ? await this.getProgressFromFirestore(userId, videoId) : null;
      
      let isConsistent = true;
      let recommendedAction: 'use_local' | 'use_cloud' | 'merge' | 'none' = 'none';
      
      if (localProgress && cloudProgress) {
        // 比較本地和雲端數據
        const timeDiff = Math.abs(localProgress.currentTime - cloudProgress.currentTime);
        const isRecentLocal = new Date(localProgress.lastWatched).getTime() > new Date(cloudProgress.lastWatched).getTime();
        
        if (timeDiff > 10) { // 時間差超過10秒認為不一致
          isConsistent = false;
          recommendedAction = isRecentLocal ? 'use_local' : 'use_cloud';
        } else {
          recommendedAction = 'use_local'; // 優先使用本地數據
        }
      } else if (localProgress) {
        recommendedAction = 'use_local';
      } else if (cloudProgress) {
        recommendedAction = 'use_cloud';
      }
      
      console.log(`🔍 進度數據驗證: ${videoId}`, {
        localProgress,
        cloudProgress,
        isConsistent,
        recommendedAction
      });
      
      return {
        localProgress,
        cloudProgress,
        isConsistent,
        recommendedAction
      };
    } catch (error) {
      console.error('❌ 驗證觀看進度數據失敗:', error);
      return {
        localProgress: null,
        cloudProgress: null,
        isConsistent: false,
        recommendedAction: 'none'
      };
    }
  }

  /**
   * 獲取用戶的所有觀看記錄
   */
  static async getUserWatchHistory(userId: string): Promise<WatchProgress[]> {
    try {
      // 這裡可以實現查詢用戶所有觀看記錄的邏輯
      // 暫時返回空數組，可以後續擴展
      return [];
    } catch (error) {
      console.error('❌ 獲取用戶觀看歷史失敗:', error);
      return [];
    }
  }

  /**
   * 清除過期的觀看進度記錄
   */
  static async clearExpiredProgress(daysToKeep: number = 30): Promise<void> {
    try {
      const localProgress = this.getLocalProgress();
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
      
      let hasChanges = false;
      Object.keys(localProgress).forEach(videoId => {
        const lastWatched = new Date(localProgress[videoId].lastWatched);
        if (lastWatched < cutoffDate) {
          delete localProgress[videoId];
          hasChanges = true;
        }
      });
      
      if (hasChanges) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(localProgress));
        console.log('🧹 清除過期的觀看進度記錄');
      }
    } catch (error) {
      console.error('❌ 清除過期觀看進度失敗:', error);
    }
  }

  /**
   * 同步本地和雲端觀看進度
   */
  static async syncProgress(userId: string): Promise<void> {
    try {
      if (!userId) return;
      
      const localProgress = this.getLocalProgress();
      const syncPromises = Object.keys(localProgress).map(async (videoId) => {
        const localData = localProgress[videoId];
        const cloudData = await this.getProgressFromFirestore(userId, videoId);
        
        // 如果雲端沒有數據，或者本地數據更新，則上傳到雲端
        if (!cloudData || new Date(localData.lastWatched) > new Date(cloudData.lastWatched)) {
          await this.saveProgressToFirestore(userId, videoId, localData);
        }
      });
      
      await Promise.all(syncPromises);
      console.log('🔄 觀看進度同步完成');
    } catch (error) {
      console.error('❌ 觀看進度同步失敗:', error);
    }
  }

  /**
   * 格式化觀看進度顯示
   */
  static formatProgress(currentTime: number, duration: number): string {
    const progress = this.calculateProgress(currentTime, duration);
    const percentage = Math.round(progress * 100);
    const currentTimeStr = this.formatTime(currentTime);
    const durationStr = this.formatTime(duration);
    
    return `${currentTimeStr} / ${durationStr} (${percentage}%)`;
  }

  /**
   * 格式化時間顯示
   */
  static formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }
}

export default WatchProgressManager;
